import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement, SlotsController } from '@phase2/outline-core';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './pc-accordion-baseline-panel.css.lit';
import '../../pc-icon/pc-icon';
/**
 * The AccordionBaselinePanel component
 * @element pc-accordion-baseline-panel
 */
@customElement('pc-accordion-baseline-panel')
export class PCAccordionBaselinePanel extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotsController(this);
  resizeObserver: ResizeObserver;

  adoptedStylesheets: AdoptedStyleSheets;

  @property({ type: String, attribute: 'variant', reflect: true })
  variant: 'primary' | 'secondary' = 'primary';

  @property({ type: String, attribute: 'aria-label' })
  ariaLabel: string;

  @property({ type: String, attribute: 'content-align' })
  contentAlign: string;

  /**
   * Wether the panel is active/open.
   * Controlled by parent accordion component.
   */
  @property({ type: Boolean, reflect: true, attribute: true })
  active = true;

  @query('.accordion-panel--content')
  content: HTMLElement;

  /**
   * Used to provided a unique ID
   * for accordion component and accessibility purposes.
   */
  @property({ type: String, reflect: true })
  id: string = Math.floor(Math.random() * 10000).toString();

  @query('.accordion-panel--button')
  buttonRef: HTMLButtonElement;

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });
  }

  firstUpdated() {
    const wrapper = this.shadowRoot?.querySelector('.accordion-panel--heading');
    if (wrapper) {
      this.resizeObserver.observe(wrapper);
    }
    this.setPanelOpenHeightVar().then(() => this.setPanelClosedHeightVar());
  }

  render(): TemplateResult {
    const isActive = this.active ? 'active' : 'inactive';

    return html`<section class="accordion-panel">
      <div class="accordion-panel--heading">
        <button
          class="accordion-panel--button ${isActive}"
          id="${this.id}-button"
          aria-expanded=${this.active}
          aria-controls="${this.id}-info"
          @click=${this.setActive}
          aria-label="expand ${this.ariaLabel}"
        >
          <div class="accordion-panel--label">
            ${this.slots.conditionalSlot('label', false)}
            ${this.slots.conditionalSlot('secondary-label', false)}
          </div>

          <span class="accordion-panel--icon ${isActive}">
            <pc-icon name="${this.active ? 'close' : 'plus'}"></pc-icon>
          </span>
        </button>
      </div>
      <div
        class="accordion-panel--content ${this.contentAlign}"
        role="region"
        aria-labelledby="${this.id}-button"
        id="${this.id}-info"
        aria-hidden="${!this.active}"
      >
        <slot></slot>
      </div>
    </section>`;
  }

  setActive() {
    this.active = !this.active;
  }

  async setPanelOpenHeightVar() {
    this.style.setProperty('--open-height', `${this.offsetHeight}px`);
    this.content.classList.add('loaded');
    return (this.active = false);
  }

  setPanelClosedHeightVar() {
    this.style.setProperty('--closed-height', `${this.offsetHeight}px`);
  }

  handleResize() {
    this.active = true;
    this.content.classList.remove('loaded');
    this.style.setProperty('--closed-height', `auto`);
    this.setPanelOpenHeightVar().then(() => this.setPanelClosedHeightVar());
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-accordion-baseline-panel': PCAccordionBaselinePanel;
  }
}
